import { useTranslate } from 'react-admin';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';
import { percentToDecimal } from 'src/pages/admin/utils/decimal-input';

import { VAULT_PARAMS_NS as NS } from './constants';
import { FieldKind, FieldSpec } from './fields';
import {
  editableFields,
  groupFields,
  isIntegerKind,
  normalizeCoins,
  normalizeNumericInput,
} from './helpers';
import type { ParamsEditor } from './useVaultParams';

const UNIT: Partial<Record<FieldKind, string>> = {
  usdc: `${NS}.units.usdc`,
  days: `${NS}.units.days`,
  seconds: `${NS}.units.seconds`,
  milliseconds: `${NS}.units.milliseconds`,
  percent: `${NS}.units.percent`,
  multiplier: `${NS}.units.multiplier`,
};

interface ParamsEditDialogProps<T> {
  title: string;
  fields: FieldSpec<T>[];
  editor: ParamsEditor<T>;
}

export const ParamsEditDialog = <T,>({ title, fields, editor }: ParamsEditDialogProps<T>) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  const renderField = ({ key, kind, label, hint, range }: FieldSpec<T>) => {
    const error = editor.errors[key];
    const rangeText = range
      ? translate(`${NS}.allowedRange`, { range: translate(range) })
      : undefined;

    if (kind === 'symbols') {
      const value = (editor.draft[key] as string[] | undefined) ?? [];
      const helper = error ? translate(error) : hint && translate(hint);
      return (
        <Box key={key} sx={{ gridColumn: '1 / -1' }}>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            size="small"
            options={[]}
            value={value}
            onChange={(_, next) =>
              // Commit on comma/whitespace or blur too, not just Enter. Trim,
              // upper-case (kPEPE excepted) and de-duplicate — see normalizeCoins.
              editor.setField(key, normalizeCoins(next.map((entry) => String(entry))))
            }
            renderTags={(tags, getTagProps) =>
              tags.map((tag, index) => (
                <Chip {...getTagProps({ index })} key={tag} size="small" label={tag} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate(label)}
                error={Boolean(error)}
                helperText={helper}
              />
            )}
          />
        </Box>
      );
    }

    const integer = isIntegerKind(kind);
    const rawValue = (editor.draft[key] as string | undefined) ?? '';
    const hintText = hint && translate(hint);
    // Show the raw fraction that will be sent so `30` in a `%` field previews
    // as `0.3`, not just the on-screen percent (spec §4.3 D1).
    let helper: string | undefined;
    if (error) {
      const errorText = translate(error);
      helper = rangeText ? `${errorText} · ${rangeText}` : errorText;
    } else {
      const parts = [hintText, rangeText].filter(Boolean) as string[];
      helper = parts.length > 0 ? parts.join(' · ') : undefined;
      if (kind === 'percent' && rawValue) {
        const preview = percentToDecimal(rawValue);
        if (preview !== null) {
          const sentAs = translate(`${NS}.sentAs`, { value: preview });
          helper = helper ? `${helper} · ${sentAs}` : sentAs;
        }
      }
    }

    return (
      <TextField
        key={key}
        fullWidth
        size="small"
        label={translate(label)}
        value={rawValue}
        error={Boolean(error)}
        helperText={helper}
        // Only IME full-width forms are normalised; anything else stays as typed
        // so validation flags it rather than the input rewriting the amount.
        onChange={(event) => editor.setField(key, normalizeNumericInput(event.target.value))}
        inputProps={{ inputMode: integer ? 'numeric' : 'decimal' }}
        InputProps={
          UNIT[kind]
            ? {
                endAdornment: (
                  <InputAdornment position="end">{translate(UNIT[kind] as string)}</InputAdornment>
                ),
              }
            : undefined
        }
      />
    );
  };

  return (
    <Dialog
      open={editor.open && editor.step === 'edit'}
      onClose={editor.saving ? undefined : editor.closeDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(title)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {editor.formErrors.length > 0 && (
            <Alert severity="error">
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {editor.formErrors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          {groupFields(editableFields(fields)).map(({ group, fields: sectionFields }) => (
            <Stack key={group ?? 'ungrouped'} spacing={1.5}>
              {group && (
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  {translate(group)}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                {sectionFields.map(renderField)}
              </Box>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={editor.closeDialog} disabled={editor.saving}>
          {translate(`${NS}.cancel`)}
        </Button>
        <Button variant="contained" onClick={editor.review} disabled={editor.saving}>
          {translate(`${NS}.review`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
